/* eslint-disable no-new */
import { apiPostImage } from '@/modules/Damage/services/damages/'
import { mapState } from 'vuex'
import { WARRANTY_MODULE_NAME } from '@/store/types'
import maSpinner from '@/components/spinner'
import flowMixin from '@/modules/Flow/mixins/flowMixin'
import Compressor from 'compressorjs'
import { prepareErrorForSentry } from '@/utils/errorHandling'

export default {
  name: 'FileImg',

  mixins: [flowMixin],

  components: {
    maSpinner
  },

  props: {
    name: {
      type: String
    },
    titleInput: {
      type: String
    },
    imageList: {
      type: Array
    }
  },

  data: function () {
    return {
      inputName: '',
      errorInput: false,
      spinner: null
    }
  },

  methods: {
    postImage(file) {
      this.spinner = true

      const reader = new FileReader()
      const imageType = this.inputName
      const sinisterId = this.getSinisterId
      const imageName = file.name
      const fileSize = file.size
      const fileExtension = file.type

      reader.readAsDataURL(file)
      reader.onload = async () => {
        const base64img = reader.result
        const imageContent = base64img.replace(
          /data:[A-z]*\/[A-z]*;base64,/,
          ''
        )
        const imgBody = { imageName, imageType, imageContent }

        let imagePostResult = {}

        try {
          imagePostResult = await apiPostImage(sinisterId, imgBody)
        } catch (error) {
          if (error.response === undefined) {
            const context = {
              component: 'fileImg',
              method: 'postImage',
              route: this.$route.path
            }

            const tags = prepareErrorForSentry(error, context, { willRedirect: true })

            this.sendSentryError(
              `Error in post image inside fileinput: ${error.message || 'Unknown error'}`,
              error.response && error.response.status >= 500 ? 'error' : 'warning',
              { ...tags, imageName, fileSize, fileExtension },
              this.getSinisterId,
              'postImage'
            )

            this.goToErrorView(error)
            return
          }

          this.throwNotification(
            this.$t('notifications.images.postImgError.title'),
            this.$t('notifications.images.postImgError.text'),
            'KO'
          )
        } finally {
          this.appendImage(imagePostResult)
        }
      }
    },

    handleChangeInput(e) {
      const maxValueFilesize = 14000000
      const acceptedFileExtensions = ['image/jpg', 'image/jpeg', 'image/png']

      this.inputName = e.target.name
      const file = e.target.files[0]

      const fileSize = file.size
      const fileExtension = file.type
      const fileName = file.name

      const isFileSizeValid = fileSize <= maxValueFilesize
      const isAcceptedFileExtension =
        acceptedFileExtensions.includes(fileExtension)

      if (!isFileSizeValid || !isAcceptedFileExtension) {
        this.appendImage({})

        this.throwNotification(
          this.$t('notifications.images.inputImgError.title'),
          this.$t('notifications.images.inputImgError.text'),
          'KO'
        )

        this.sendSentryEvent(
          'Error with the size or extension of the image',
          this.handleChangeInput.name,
          { fileName, fileExtension, fileSize }
        )
        return
      }

      // Threshold lowered from 6MB to 4MB to trigger optimization earlier
      if (fileSize >= 4000000) {
        this.resizeAndCompressImage(file)
      } else {
        this.postImage(file)
      }
    },

    /**
     * Resize image before compressing to reduce memory usage
     * Maximum 2048x2048px maintaining aspect ratio
     */
    async resizeAndCompressImage(file) {
      const fileSize = file.size
      const fileExtension = file.type
      const fileName = file.name

      try {
        // Get original image dimensions
        const imageDimensions = await this.getImageDimensions(file)

        // If dimension detection failed, use fallback compression
        if (imageDimensions === null) {
          this.sendSentryEvent(
            'Dimension detection failed, using fallback compression',
            this.resizeAndCompressImage.name,
            { fileName, fileSize, fileExtension, userAgent: navigator.userAgent }
          )
          this.compressImageWithFallback(file, fileSize, fileName, fileExtension)
          return
        }

        // Resize first
        const resizedFile = await this.resizeImage(file, imageDimensions)

        this.compressImage(resizedFile, file, fileSize, fileName, fileExtension, imageDimensions)
      } catch (error) {
        this.throwNotification(
          this.$t('notifications.images.uploadError.title'),
          this.$t('notifications.images.uploadError.text'),
          'KO'
        )

        this.appendImage({})

        const additionalData = {
          error: error.message || error,
          fileName,
          fileSize,
          fileExtension,
          step: 'resizeAndCompress'
        }

        this.sendSentryEvent(
          `Error in resize and compress image (${error.message || error})`,
          this.resizeAndCompressImage.name,
          additionalData
        )
      }
    },

    /**
     * Get image dimensions using multiple fallback strategies
     * 1. Try createImageBitmap (more memory efficient, better format support)
     * 2. Fallback to Image element
     * 3. If both fail, return null to trigger fallback compression
     */
    async getImageDimensions(file) {
      // Strategy 1: Use createImageBitmap (better memory efficiency and format support)
      if (typeof createImageBitmap === 'function') {
        try {
          const bitmap = await createImageBitmap(file)
          const dimensions = { width: bitmap.width, height: bitmap.height }
          bitmap.close() // Free memory
          return dimensions
        } catch (bitmapError) {
          // Continue to fallback strategy
        }
      }

      // Strategy 2: Use Image element (traditional approach)
      try {
        const dimensions = await this.getImageDimensionsViaImageElement(file)
        return dimensions
      } catch (imgError) {
        // Both strategies failed - return null to trigger fallback
        return null
      }
    },

    /**
     * Get dimensions using Image element (fallback method)
     */
    getImageDimensionsViaImageElement(file) {
      return new Promise((resolve, reject) => {
        const img = new Image()
        const url = URL.createObjectURL(file)

        // Add timeout to prevent hanging on problematic files
        const timeout = setTimeout(() => {
          URL.revokeObjectURL(url)
          reject(new Error('Image load timeout'))
        }, 10000)

        img.onload = () => {
          clearTimeout(timeout)
          URL.revokeObjectURL(url)
          resolve({ width: img.width, height: img.height })
        }

        img.onerror = () => {
          clearTimeout(timeout)
          URL.revokeObjectURL(url)
          reject(new Error('Failed to load image'))
        }

        img.src = url
      })
    },

    /**
     * Resize image to maximum 2048x2048px maintaining aspect ratio
     */
    resizeImage(file, dimensions) {
      const MAX_DIMENSION = 2048
      
      // If image is already smaller than max, no need to resize
      if (dimensions.width <= MAX_DIMENSION && dimensions.height <= MAX_DIMENSION) {
        return Promise.resolve(file)
      }

      return new Promise((resolve, reject) => {
        new Compressor(file, {
          maxWidth: MAX_DIMENSION,
          maxHeight: MAX_DIMENSION,
          mimeType: file.type,
          quality: 1, // No compression at this stage, only resize
          success: (result) => {
            resolve(result)
          },
          error: (error) => {
            reject(error)
          }
        })
      })
    },

    /**
     * Compress image with variable quality
     * - quality 0.8 for files <= 8MB
     * - quality 0.6 for files > 8MB (more aggressive compression)
     */
    compressImage(file, originalFile, originalFileSize, originalFileName, originalFileExtension, dimensions) {
      const quality = originalFileSize > 8000000 ? 0.6 : 0.8

      new Compressor(file, {
        quality,
        success: (result) => {
          this.postImage(result)
        },
        error: (error) => {
          this.sendSentryMessage(
            this.$options.name,
            `compressImage failed, attempting fallback: ${error.message || error}`,
            []
          )

          this.compressImageWithFallback(
            originalFile || file,
            originalFileSize,
            originalFileName,
            originalFileExtension
          )
        }
      })
    },

    /**
     * Fallback compression when dimension detection fails
     * Uses CompressorJS with maxWidth/maxHeight which handles dimensions internally
     * This is safer for problematic files (HEIC disguised as JPEG, etc.)
     */
    compressImageWithFallback(file, originalFileSize, originalFileName, originalFileExtension) {
      const MAX_DIMENSION = 2048
      const quality = originalFileSize > 8000000 ? 0.6 : 0.8

      new Compressor(file, {
        maxWidth: MAX_DIMENSION,
        maxHeight: MAX_DIMENSION,
        quality,
        // checkOrientation helps with mobile photos that have EXIF rotation
        checkOrientation: true,
        success: (result) => {
          this.postImage(result)
        },
        error: (error) => {
          this.throwNotification(
            this.$t('notifications.images.uploadError.title'),
            this.$t('notifications.images.uploadError.text'),
            'KO'
          )

          this.appendImage({})

          this.sendSentryEvent(
            `Fallback compression failed: ${error.message || error}`,
            this.compressImageWithFallback.name,
            {
              error: error.message || error,
              fileName: originalFileName,
              fileSize: originalFileSize,
              fileExtension: originalFileExtension,
              step: 'fallbackCompress'
            }
          )
        }
      })
    },

    appendImage(image) {
      // append image to the list and empty the input file selector
      this.$emit('file:src', image)
      this.$el.querySelector('input.file').value = null

      // remove spinner and error
      this.spinner = false
    }
  },
  computed: {
    ...mapState(WARRANTY_MODULE_NAME, {
      stateWarrantySinisterId: 'sinisterId'
    })
  },
  watch: {
    value(newVal) {
      if (newVal) {
        this.fileName = newVal.name
      }
    }
  }
}